import React from 'react';
import { motion } from 'framer-motion';
import CTA from './CTA';

const categories = [
  {
    title: 'Personalized Keepsakes',
    desc: 'Customized treasures that guests will cherish forever, beautifully crafted with personal touches that reflect your unique love story.',
    img: '/img/i25.jpg',
    points: [
      'Custom engraving options',
      'Personalized packaging',
      'Unique design elements',
      'Premium materials',
    ],
  },
  {
    title: 'Luxury Collections',
    desc: 'Premium gifts that reflect the elegance and sophistication of your celebration, curated from the finest artisans worldwide.',
    img: '/img/i26.jpg',
    points: [
      'High-end materials',
      'Exclusive designs',
      'Elegant presentation',
      'Limited editions',
    ],
  },
  {
    title: 'Cultural Traditions',
    desc: 'Meaningful items that honor heritage and celebrate cultural customs with authenticity and reverence for tradition.',
    img: '/img/i27.jpg',
    points: [
      'Traditional craftsmanship',
      'Cultural significance',
      'Authentic materials',
      'Heritage designs',
    ],
  },
];

export default function Gifting() {
  return (
    <div className="bg-white">
      {/* Hero - Image Background */}
      <section className="relative h-screen min-h-[600px] max-h-[900px] overflow-hidden pt-20 sm:pt-0">
        <img
          src="/img/i28.jpg"
          alt="Luxury Wedding Gifting"
          className="absolute inset-0 w-full h-full object-cover scale-105 transition-transform duration-[15s] ease-out"
        />
        <div className="absolute inset-0 bg-black/55" />


        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 h-full flex items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-white/70 text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase mb-6 sm:mb-8"
            >
              Exquisite Wedding Gifting
            </motion.div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-white leading-tight mb-6 sm:mb-10">
              Luxury Wedding Gifting Collections
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-sm sm:text-base md:text-lg text-white/90 max-w-4xl mx-auto leading-relaxed mb-8 sm:mb-12"
            >
              Curated collections of exquisite gifts that express gratitude and create lasting memories. Our premium gifting solutions add elegance to your special celebrations, honoring your guests with thoughtful treasures.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="flex items-center justify-center"
            >
              <a href="#collections" className="group relative px-8 sm:px-12 py-4 sm:py-6 border-2 border-white/80 text-white text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.4em] uppercase bg-transparent hover:bg-white hover:text-black transition-all duration-500 rounded-sm backdrop-blur-sm">
                <span className="pointer-events-none absolute -top-5 left-1/2 -translate-x-1/2 hidden sm:flex items-center gap-4 text-white group-hover:text-black transition-colors duration-300">
                  <span className="block h-px w-16 bg-white/60 group-hover:bg-black/60 transition-colors" />
                  <span className="block w-3 h-3 rounded-full border-2 border-white/70 group-hover:border-black/70 transition-colors" />
                  <span className="block h-px w-16 bg-white/60 group-hover:bg-black/60 transition-colors" />
                </span>
                EXPLORE COLLECTIONS
                <div className="absolute inset-0 bg-white/5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </a>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Floating decorative elements */}
        <div className="absolute bottom-10 left-10 opacity-30">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
        </div>
        <div className="absolute top-20 right-20 opacity-20">
          <div className="w-1 h-1 bg-white rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
      </section>

      {/* Collections - Matching home page services style */}
      <section id="collections" className="py-24 bg-gradient-to-b from-[#395958] to-[#2a4342] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true, margin: '-100px' }}
            className="text-center mb-20"
          >
            <div className="uppercase tracking-[0.4em] text-xs text-[#F1D291] mb-6">Our Collections</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white mb-8 leading-tight">
              Curated Gifting<br />
              <span className="text-white/80">Collections</span>
            </h2>
            <p className="text-base md:text-lg text-white/80 max-w-4xl mx-auto leading-relaxed">
              From personalized keepsakes to luxury collections, each gift is thoughtfully selected to honor your guests and celebrate the beauty of your special day.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {categories.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: "easeOut" }}
                viewport={{ once: true, margin: '-100px' }}
                className="group relative overflow-hidden rounded-2xl border border-white/20 bg-white/10 backdrop-blur-sm shadow-[0_25px_50px_rgba(0,0,0,0.3)] hover:shadow-[0_35px_70px_rgba(0,0,0,0.4)] transition-all duration-500"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={item.img} 
                    alt={item.title} 
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/0 to-black/50 group-hover:from-black/20 group-hover:to-black/60 transition-all duration-500" />
                  
                  {/* Floating icon */}
                  <div className="absolute top-6 left-6">
                    <div className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-300">
                      <div className="w-6 h-6 bg-black rounded-full"></div>
                    </div>
                  </div>
                </div>
                
                <div className="p-8">
                  <h3 className="text-xl font-serif text-white mb-3 group-hover:text-[#F1D291] transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="text-white/80 text-sm leading-relaxed mb-6">
                    {item.desc}
                  </p>
                  
                  <div className="space-y-2 mb-6">
                    {item.points.map((point, idx) => (
                      <div key={idx} className="flex items-center text-xs text-white/70">
                        <div className="w-1 h-1 bg-[#F1D291] rounded-full mr-3 group-hover:bg-white transition-colors duration-300"></div>
                        {point}
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex items-center text-xs uppercase tracking-[0.3em] text-white/60 group-hover:text-white transition-colors duration-300">
                    <span>Explore Collection</span>
                    <div className="ml-2 w-4 h-4 border border-white/40 rounded-full flex items-center justify-center group-hover:border-white transition-colors duration-300">
                      <div className="w-1 h-1 bg-white/60 rounded-full group-hover:bg-white transition-colors duration-300"></div>
                    </div>
                  </div>
                </div>
                
                {/* Decorative corner elements */}
                <div className="absolute top-4 right-4 w-8 h-8 border-t border-r border-white/30 group-hover:border-white/60 transition-colors duration-300" />
                <div className="absolute bottom-4 left-4 w-8 h-8 border-b border-l border-white/30 group-hover:border-white/60 transition-colors duration-300" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Thoughtful Gifting - Stats style section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true, margin: '-100px' }}
            className="text-center mb-16"
          >
            <div className="uppercase tracking-[0.4em] text-xs text-[#395958] mb-6">Thoughtful Gifting</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-gray-900 mb-8 leading-tight">
              Meaningful<br />
              <span className="text-gray-700">Connections</span>
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Our wedding gifting collections are meticulously curated to offer meaningful and memorable tokens of appreciation. Each piece reflects our commitment to quality, elegance, and the personal touch that makes your celebration truly special.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: 'Curated Excellence',
                desc: 'Every gift is handpicked for its quality, beauty, and meaning',
                features: ['Premium materials', 'Artisan craftsmanship', 'Timeless designs', 'Quality assurance']
              },
              {
                title: 'Personal Touch',
                desc: 'Customization options that make each gift uniquely yours',
                features: ['Custom engraving', 'Personalized packaging', 'Bespoke designs', 'Individual attention']
              }
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: "easeOut" }}
                className="group relative p-10 text-center rounded-2xl bg-gray-50 shadow-[0_25px_50px_rgba(0,0,0,0.1)] border border-gray-200 hover:shadow-[0_35px_70px_rgba(0,0,0,0.15)] transition-all duration-500 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-gray-100/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10">
                  <h3 className="text-2xl font-serif text-gray-900 mb-4 group-hover:scale-105 transition-transform duration-300">{item.title}</h3>
                  <p className="text-base text-gray-600 mb-6">{item.desc}</p>
                  <div className="space-y-2">
                    {item.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center justify-center text-sm text-gray-600">
                        <div className="w-1 h-1 bg-[#395958] rounded-full mr-3"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Decorative corner */}
                <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-gray-300 group-hover:border-gray-400 transition-colors duration-300" />
                <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-gray-300 group-hover:border-gray-400 transition-colors duration-300" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTA />
    </div>
  );
}